import { ref, computed, reactive, toRefs, nextTick } from 'vue'
import { isArray } from '@/common/js/utils.js'
import eventBus from '@/event-bus.js'
import { Message } from 'view-design'

import { adAccount } from '@/api/common.model'
import { getAudience, getDevice, getWechatPositions, getUnionPositionPackages } from '@/api/tencent.model'

import androidVersion from '@/data/tencent/android_version'
import iosVersion from '@/data/tencent/ios_version'
import SITESET_CONST, {
  SITE_ENUM, DEFAULT_SITES, SEARCH_AD_DEFAULT_SITES,
  SITESET_QBSEARCH_SCENES, SITESET_WECHAT_SEARCH_SCENES,
  WECHAT_SITES, SITESET_PCQQ_SCENES,
  EXPLORATION_ENUM
} from '@/views/create/gdt/model/siteset/siteset-const.js'
import { getGdtv3TargetingForm } from '../../model/gdtv3form.js'
import { setLocal, getLocal } from '@/common/js/api.js'
import { MEDIAS } from '@/common/js/constants.js'
import { AD_MARKETING_SCENE } from '@/views/create/gdt/model/project-const.js'

const getAllSiteSet = () => SITESET_CONST.SITE_SET_LIST.map((s) => s.label)
const targetingForm = ref({})

const isAutomaticSiteEnabled = computed(() => targetingForm.value.automatic_site_enabled)
// 是否微信流量
const isWechatStream = computed(() => WECHAT_SITES.some((s) => targetingForm.value.siteSet?.includes(s)))
// 是否包含视频号版位
const hasChannelSiteset = computed(() => targetingForm.value.siteSet.includes(SITE_ENUM.CHANNELS))
// 包含视频号版位 或 自动版位时禁用
const locationTypeDisabled = computed(() => hasChannelSiteset.value || isAutomaticSiteEnabled.value)

export function getSiteSetInitList() {
  // 获取场景定向标签 https://developers.e.qq.com/docs/api/tools/scene_spec_tags/scene_spec_tags_get?version=1.3
  return Promise.all([
    getWechatPositions({ ad_account_id: 5165, type: 'WECHAT_POSITION' }),
    getWechatPositions({ ad_account_id: 5165, type: 'OFFICIAL_ACCOUNT_MEDIA_CATEGORY' }),
    getWechatPositions({ ad_account_id: 5165, type: 'MINI_PROGRAM_AND_MINI_GAME' }),
    getWechatPositions({ ad_account_id: 5165, type: 'PAY_SCENE' }),
    getWechatPositions({ ad_account_id: 5165, type: 'WECHAT_CHANNELS_SCENE' })
  ])
}
export function getInitForm(props) {
  const { defaultAccount } = props || {}
  //  有默认账号使用默认账号，没有默认账号使用本地缓存账号
  const account = defaultAccount?.id ? defaultAccount : getLocal('gdtTargetingFormAccount')
  const form = {
    account: {}, // 选择的账号
    name: '', // 定向名称
    description: '', // 描述
    ...getGdtv3TargetingForm(account)
  }

  if (account?.id) {
    form.account = account
  }

  return form
}

/**
 * @description: 初始化腾讯广告3.0定向包表单
 * @return {*}
 */
export function useInitialTargetingForm() {
  targetingForm.value = { ...getInitForm() }
  return {
    targetingForm
  }
}

export function useAccountList({ getDeviceData }) {
  const accountList = ref([])

  const getAccounts = () => {
    adAccount({ mediaId: MEDIAS.GDT, authFlag: 1 }).then((data) => {
      accountList.value = data
    })
  }

  getAccounts()

  /**
   * @description: 切换账号
   */
  function onAccountChange(e) {
    // 切换账号时，构建人群包需要的value结构
    targetingForm.value.populations = {
      [e.id]: {
        accountId: e.id,
        includes: [],
        excludes: []
      }
    }
    // getAudienceData(e.id)
    getDeviceData(e.id)
  }

  return {
    accountList,
    onAccountChange
  }
}

/**
 * @description: 人群包相关
 * @return {*}
 */
export function useAudience() {
  const audienceModel = reactive({
    audiencesData: [],
    audienceLoading: false
  })

  const populations = computed({
    get() {
      return targetingForm.value.populations
    },
    set(vals) {
      const { accountId } = vals
      const newForm = targetingForm.value
      newForm.populations = {
        ...newForm.populations,
        [accountId]: vals
      }
      targetingForm.value = newForm
    }
  })

  const getAudienceData = (ad_account_id) => {
    if (!ad_account_id) return
    audienceModel.audienceLoading = true
    getAudience({ ad_account_id }).then((data) => {
      audienceModel.audiencesData = data.map((au) => ({
        ...au,
        check: null,
        includesDisabled: false,
        excludesDisabled: false,
        groupId: au.audience_id,
        groupName: au.name,
        userCount: au.user_count
      }))
      audienceModel.audienceLoading = false
    })
  }

  return {
    ...toRefs(audienceModel),
    populations,
    getAudienceData
  }
}

export const useAge = () => {
  const ageRangeText = computed(() => {
    let [start, end] = targetingForm.value.ageRange
    if (end === 66) {
      end = '66岁及以上'
    } else {
      end = `${end}岁`
    }
    return `${start}岁 - ${end}`
  })
  return {
    ageRangeText
  }
}

/**
 * @description: 地域相关
 * @return {*}
 */
export const useRegion = () => {
  const cityData = ref([])
  // 城市列表数据
  fetch('/data/tencent_region.json')
    .then((res) => res.json())
    .then((cities) => (cityData.value = cities))

  // 地域数据，微信流量、自动版位过滤掉港澳台
  const filterdCityData = computed(() => {
    if (isWechatStream.value || isAutomaticSiteEnabled.value) {
      const regions = cityData.value.filter((c) => {
        c.children = c.children.filter((child) => !['台湾省', '香港特别行政区', '澳门特别行政区'].includes(child.value))
        return c.value === '中国'
      })
      return regions
    }
    return cityData.value
  })
  return {
    filterdCityData,
    locationTypeDisabled
  }
}

export const useDevice = () => {
  const deviceBrandData = ref([])

  const getDeviceData = (ad_account_id) => {
    getDevice({ ad_account_id }).then((data) => {
      // 递归修改数据结构
      const loop = (list) => {
        return list.map((res) => {
          const item = {
            id: Number(res.id),
            key: Number(res.id),
            value: res.value
          }

          if (res.children?.length) {
            item.children = loop(res.children)
          }
          return item
        })
      }

      if (isArray(data)) {
        deviceBrandData.value = loop(data)
      }
    })
  }
  // 初始化获取设备品牌信息， 用于复制、修改定向
  targetingForm.value.account?.id && getDeviceData(targetingForm.value.account.id)

  return {
    deviceBrandData,
    getDeviceData
  }
}

export const useDeviceOS = () => {
  return {
    deviceVersionData: [...androidVersion, ...iosVersion]
  }
}

/**
 * @description: 排除已转化
 * @return {*}
 */
export const useExcludeConversion = () => {
  const onBehaviorChange = (list) => {
    if (!list?.length) {
      nextTick(() => {
        const excluded_converted_audience = targetingForm.value.excluded_converted_audience
        excluded_converted_audience.conversion_behavior_list = ['OPTIMIZATIONGOAL_FOLLOW']
        targetingForm.value = {
          ...targetingForm.value,
          excluded_converted_audience
        }
        Message.warning('请至少选择一个行为')
      })
    }
  }
  return {
    onBehaviorChange
  }
}

/**
 * @description: 版位相关
 * @return {*}
 */
export const useSiteSet = () => {
  const siteSetModel = reactive({
    unionPackageLoading: false,
    currUnionPackageAccountId: 0,
    unionPositionPackages: [],
    unionIncludePositionPackages: [],
    unionExcludePositionPackages: [],
    setSetInitData: {
      wechatPositions: [],
      wechatChannelPositions: [],
      officialAccountMediaCates: [],
      miniGames: [],
      payScenes: [],
      pcScenes: SITESET_PCQQ_SCENES,
      qbSearchScenes: SITESET_QBSEARCH_SCENES,
      wechatSearchScenes: SITESET_WECHAT_SEARCH_SCENES
    }
  })
  const showWechatChannels = computed(() => {
    const { siteSet, automatic_site_enabled } = targetingForm.value
    return siteSet.includes(SITE_ENUM.CHANNELS) || automatic_site_enabled
  })

  const excludesUnionPositionPackages = computed({
    get() {
      const selected = targetingForm.value.scene_spec.exclude_union_position_package[siteSetModel.currUnionPackageAccountId]
      return selected || []
    },
    set(vals) {
      const newForm = targetingForm.value
      newForm.scene_spec.exclude_union_position_package[siteSetModel.currUnionPackageAccountId] = vals
      targetingForm.value = { ...newForm }
    }
  })

  const includesUnionPositionPackages = computed({
    get() {
      const selected = targetingForm.value.scene_spec.include_union_position_package[siteSetModel.currUnionPackageAccountId]
      return selected || []
    },
    set(vals) {
      const newForm = targetingForm.value
      newForm.scene_spec.include_union_position_package[siteSetModel.currUnionPackageAccountId] = vals
      targetingForm.value = { ...newForm }
    }
  })

  const showPaySceneTips = computed(() => {
    // 1024793 对应微信广告位置微信支付订单详情页 的id
    const { wechatPostion } = targetingForm.value
    return wechatPostion.length && !wechatPostion.includes(1024793)
  })

  // 显示场景提示
  const showMiniGameTips = computed(() => {
    // [1024794, 1024795, 1024796, 1024797] 对应微信广告位置 小程序banner | 广告小程序激励式广告 | 小程序插屏广告 |  小程序封面广告 的id
    const { wechatPostion } = targetingForm.value
    return wechatPostion.length && !wechatPostion.some((w) => [1024794, 1024795, 1024796, 1024797].includes(w))
  })

  const onAccountItemChange = (account) => {
    siteSetModel.currUnionPackageAccountId = account.id
    getUnionPackages()
  }

  // 切换版位类型
  const onSiteSetTypeChange = (e) => {
    // 广播自动版位发生了切换
    eventBus.$emit('automaticSiteEnabledChange')

    // 换到自动版位时，自动地域为【常住】
    if (isAutomaticSiteEnabled.value) {
      targetingForm.value.locationTypes = ['LIVE_IN']
    }
    // 切换时,默认全选
    if (targetingForm.value.ad_marketing_scene === AD_MARKETING_SCENE.VERSION_SEARCH) {
      targetingForm.value.siteSet = SEARCH_AD_DEFAULT_SITES
    } else {
      targetingForm.value.siteSet = e ? DEFAULT_SITES : getAllSiteSet()
    }
  }

  const onAdMarketingSceneChange = (e) => {
    if (e === AD_MARKETING_SCENE.VERSION_SMART) {
      targetingForm.value.automatic_site_enabled = true
      targetingForm.value.exploration_strategy = EXPLORATION_ENUM.AUTOMATIC
    }

    if (e === AD_MARKETING_SCENE.VERSION_SEARCH) {
      targetingForm.value.automatic_site_enabled = false
    }
    onSiteSetTypeChange(true)
  }

  // 切换版位
  const onSiteSetItemChange = (sites) => {
    // 广播手动版位发生了变化
    eventBus.$emit('sitesetChange')

    // 微信流量清空地域
    if (isWechatStream.value) {
      targetingForm.value.region = []
    }

    // 包含视频号只能选择常住这里的人
    if (sites.includes(SITE_ENUM.CHANNELS)) {
      targetingForm.value.locationTypes = ['LIVE_IN']
    }

    if (!sites.includes(SITE_ENUM.MOBILE_UNION)) {
      targetingForm.value = {
        ...targetingForm.value,
        // 优量汇广告展示场景切换到不限，关闭流量优选
        excludeUnionType: 0,
        flow_optimization_enabled: false,
        displaySceneType: 0
      }
    }

    if (!sites.includes(SITE_ENUM.WECHAT)) {
      targetingForm.value = {
        ...targetingForm.value,
        // 微信公众号与小程序定投、场景切换到不限
        wechat_position: 0,
        wechat_scene: 0,
        wechatPostion: []
      }
    }
  }
  /**
   * @description: 切换到自定义时，请求第一个账号的数据
   * @param {*} val
   */
  const onUnionPackageTypeChange = (val) => {
    if (val === 1) {
      siteSetModel.currUnionPackageAccountId = targetingForm.value.account.id
      getUnionPackages()
    }
  }

  const onOfficialAccountMediaCateChange = (v) => {
    toggleWechatSceneCheckStates(v, 1, 'officialAccountMediaCate')
  }
  const onPaySceneChange = (v) => {
    toggleWechatSceneCheckStates(v, 3000000, 'payScene')
  }
  const onMiniGameChange = (v) => {
    toggleWechatSceneCheckStates(v, 1001000, 'miniGame')
  }

  function toggleWechatSceneCheckStates(v, needle, prop) {
    const [firstLabel] = v
    const newForm = targetingForm.value
    // 不限的label = needle， 每组不限的id都不一致
    // v.length > 1 防止没有任何勾选时，点击不限选不上
    if (firstLabel === needle && v.length > 1) {
      newForm[prop] = v.filter((v) => v !== needle)
    } else {
      if (v.includes(needle)) {
        newForm[prop] = [needle]
      }
    }

    targetingForm.value = newForm
  }

  // 获取场景定向标签
  async function getSiteSetInitData() {
    const data = await getSiteSetInitList()
    // 获取场景定向标签 https://developers.e.qq.com/docs/api/tools/scene_spec_tags/scene_spec_tags_get?version=1.3

    const [wechatPositions, officialAccountMediaCates, miniGames, payScenes, wechatChannelPositions] = data
    if (wechatPositions?.list && isArray(wechatPositions.list)) {
      siteSetModel.setSetInitData.wechatPositions = wechatPositions.list
    }

    if (wechatChannelPositions?.list && isArray(wechatChannelPositions.list)) {
      // 20250217 tg4.49 过滤掉【2100489-视频号评论区广告】
      const list = wechatChannelPositions.list.filter(i => ![2100489].includes(i.id))
      siteSetModel.setSetInitData.wechatChannelPositions = list
    }

    if (officialAccountMediaCates?.list && isArray(officialAccountMediaCates.list)) {
      siteSetModel.setSetInitData.officialAccountMediaCates = officialAccountMediaCates.list
    }

    if (miniGames?.list && isArray(miniGames.list)) {
      siteSetModel.setSetInitData.miniGames = miniGames.list
    }

    if (payScenes?.list && isArray(payScenes.list)) {
      siteSetModel.setSetInitData.payScenes = payScenes.list
    }
  }

  async function getUnionPackages() {
    siteSetModel.unionPackageLoading = true

    const [includePackages, excludePackages] = await Promise.all([
      getUnionPositionPackages({ ad_account_id: siteSetModel.currUnionPackageAccountId, union_package_type: 'UNION_PACKAGE_TYPE_INCLUDE' }),
      getUnionPositionPackages({ ad_account_id: siteSetModel.currUnionPackageAccountId, union_package_type: 'UNION_PACKAGE_TYPE_EXCLUDE' })
    ])

    if (includePackages && includePackages?.length) {
      siteSetModel.unionIncludePositionPackages = includePackages.map((p) => ({
        id: p.union_package_id,
        value: p.union_package_name
      }))
    }

    if (excludePackages && excludePackages?.length) {
      siteSetModel.unionExcludePositionPackages = excludePackages.map((p) => ({
        id: p.union_package_id,
        value: p.union_package_name
      }))
    }

    siteSetModel.unionPackageLoading = false
  }

  getSiteSetInitData()

  return {
    ...toRefs(siteSetModel),
    showWechatChannels,
    includesUnionPositionPackages,
    excludesUnionPositionPackages,
    showPaySceneTips,
    showMiniGameTips,
    getUnionPackages,
    onAccountItemChange,
    onSiteSetTypeChange,
    onAdMarketingSceneChange,
    onSiteSetItemChange,
    onMiniGameChange,
    onPaySceneChange,
    onOfficialAccountMediaCateChange,
    onUnionPackageTypeChange
  }
}

/**
 * @description: 腾讯广告搜索广告关键词
 * @return {*}
 */
export const useGdtSearchBidKeywords = () => {
  const searchAdKeywordModel = reactive({
    showSearchBidKeywordModal: false,
    searchBidKeywordList: []
  })

  function onSearchKeywordAdd() {
    searchAdKeywordModel.showSearchBidKeywordModal = true
    searchAdKeywordModel.searchBidKeywordList = targetingForm.value.searchAdBidKeywords
  }
  function onSearchKeywordSelectConfirm(words = []) {
    if (Array.isArray(words) && words?.length) {
      targetingForm.value.searchAdBidKeywords = words.slice(0, 1000)
    } else {
      targetingForm.value.searchAdBidKeywords = []
    }
  }
  return {
    ...toRefs(searchAdKeywordModel),
    onSearchKeywordAdd,
    onSearchKeywordSelectConfirm
  }
}
